"use client"

import { useState, useRef } from "react"
import type { HeroInventory, ItemDefinition, InventoryItem } from "@/types/item.types"
import { getItemDefinition, canUseItem } from "@/systems/item-system"
import { playArmorDropSound } from "@/hooks/use-sound-effects"

interface MobileInventoryPanelProps {
  isOpen: boolean
  onClose: () => void
  inventory: HeroInventory
  onUseItem: (slotIndex: number) => void
  onDropItem: (slotIndex: number) => void
  pendingItemSlot?: number | null
}

const rarityColors: Record<string, string> = {
  common: "text-gray-300",
  rare: "text-blue-400",
  epic: "text-purple-400",
  legendary: "text-amber-400",
  unique: "text-orange-400",
}

const effectTypeLabels: Record<string, string> = {
  heal: "Heal",
  mana: "Mana",
  "damage-boost": "Damage",
  "speed-boost": "Speed",
  teleport: "Teleport",
  lure: "Lure",
  absorb: "Shield",
  armor: "Armor",
  lifesteal: "Lifesteal",
  "spell-power": "Spell Power",
  "max-health": "Max Health",
}

// Component to render item icon from individual image file or spritesheet
const ItemSpriteIcon = ({
  itemDef,
  size,
  showOutline = false,
}: {
  itemDef: ItemDefinition
  size: number
  showOutline?: boolean
}) => {
  // Use the direct image path if available (for beacon and other items)
  if (itemDef.image) {
    return (
      <img
        src={itemDef.image}
        alt={itemDef.name}
        width={size}
        height={size}
        style={{
          imageRendering: "pixelated",
          filter: showOutline ? "drop-shadow(0 0 2px white)" : undefined
        }}
        className="object-contain"
      />
    )
  }

  // Fallback to colored placeholder
  return (
    <div
      className="rounded"
      style={{
        width: size,
        height: size,
        backgroundColor: itemDef.iconColor,
      }}
    />
  )
}

// Detailed item view for drilldown
const ItemDetailView = ({
  itemDef,
  slot,
  slotIndex,
  onBack,
  onDrop,
}: {
  itemDef: ItemDefinition
  slot: InventoryItem
  slotIndex: number
  onBack: () => void
  onDrop: (slotIndex: number) => void
}) => {
  const hasCooldown = itemDef.cooldown && itemDef.cooldown > 0
  const isOnCooldown = slot.currentCooldown > 0
  const isBeacon = slot.itemDefId === "clone-activation-beacon"

  const handleDrop = () => {
    // Play armor drop sound for equipment
    if (itemDef.type !== "consumable") {
      playArmorDropSound()
    }
    onDrop(slotIndex)
    onBack()
  }

  return (
    <div className="p-4">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-4 text-white/60 hover:text-white/80 transition-colors active:text-white"
      >
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-sm">Back</span>
      </button>

      {/* Item icon and header */}
      <div className="flex items-start gap-4 mb-4">
        <div
          className="relative rounded-lg overflow-hidden shrink-0 flex items-center justify-center"
          style={{
            width: 80,
            height: 80,
            backgroundColor: itemDef.iconColor,
            border: isBeacon ? "3px solid #3b82f6" : "3px solid #fbbf24",
            boxShadow: isBeacon ? "0 0 12px rgba(59, 130, 246, 0.4)" : undefined,
          }}
        >
          <ItemSpriteIcon itemDef={itemDef} size={64} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`font-bold text-lg mb-1 ${isBeacon ? "text-blue-400" : rarityColors[itemDef.rarity]}`}>{itemDef.name}</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 text-sm capitalize">{itemDef.rarity}</span>
            <span className="text-gray-600">-</span>
            <span className="text-gray-400 text-sm capitalize">{itemDef.type}</span>
            {isBeacon ? (
              <span className="text-blue-400 text-sm bg-blue-500/20 px-2 py-0.5 rounded">
                {slot.charges || 0} charges
              </span>
            ) : slot.quantity > 1 ? (
              <span className="text-white/60 text-sm bg-white/10 px-2 py-0.5 rounded">
                x{slot.quantity}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-300 text-sm leading-relaxed mb-4">{itemDef.description}</p>

      {/* Beacon-specific info */}
      {isBeacon && (
        <div className="bg-blue-900/30 rounded-lg p-3 space-y-2 mb-4 border border-blue-500/30">
          <h4 className="text-blue-300 text-xs font-semibold uppercase tracking-wider mb-2">Clone Status</h4>
          <div className="flex justify-between items-center">
            <span className="text-gray-400 text-sm">Stored Clones</span>
            <span className="text-blue-400 font-semibold">{slot.charges || 0}</span>
          </div>
          <p className="text-gray-500 text-xs mt-2">
            Purchase clones via the droppod menu to increase charges
          </p>
        </div>
      )}

      {/* Effects */}
      {!isBeacon && itemDef.effects.length > 0 && (
        <div className="bg-black/30 rounded-lg p-3 space-y-2 mb-4">
          <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">Effects</h4>
          {itemDef.effects.map((effect, idx) => (
            <div key={idx} className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">{effectTypeLabels[effect.type] || effect.type}</span>
              <span className={effect.value > 0 ? "text-green-400 font-semibold" : "text-red-400 font-semibold"}>
                {effect.type === "lifesteal" || effect.type === "spell-power"
                  ? `${(effect.value * 100).toFixed(0)}%`
                  : effect.value > 0
                    ? `+${effect.value}`
                    : effect.value}
                {effect.duration && ` (${(effect.duration / 60).toFixed(1)}s)`}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Cooldown */}
      {hasCooldown && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-gray-500 text-sm">Cooldown</span>
          <span className={isOnCooldown ? "text-red-400 font-semibold" : "text-yellow-400 font-semibold"}>
            {isOnCooldown
              ? `${Math.ceil(slot.currentCooldown / 60)}s remaining`
              : `${(itemDef.cooldown! / 60).toFixed(1)}s`}
          </span>
        </div>
      )}

      {/* Value - don't show for beacon */}
      {!isBeacon && (
        <div className="flex items-center justify-between">
          <span className="text-gray-500 text-sm">Value</span>
          <span className="text-amber-400 font-semibold">{itemDef.cost} gold</span>
        </div>
      )}

      {/* Usage hint */}
      <div className="mt-4 text-gray-500 text-sm bg-white/5 px-3 py-2 rounded">
        {isBeacon
          ? "Cannot be removed or sold - tracks your stored clones"
          : itemDef.type === "consumable"
            ? "Tap to use from inventory"
            : "Equipment provides passive bonuses"}
      </div>

      {/* Drop button - only show for non-beacon items */}
      {!isBeacon && (
        <button
          onClick={handleDrop}
          className="mt-4 w-full bg-red-600/20 hover:bg-red-600/30 active:bg-red-600/40 text-red-400 font-semibold py-3 px-4 rounded-lg border border-red-500/30 transition-colors"
        >
          Drop Item
        </button>
      )}
    </div>
  )
}

const ItemButton = ({
  slot,
  itemDef,
  slotIndex,
  inventory,
  isPending,
  onUseItem,
  onClose,
  onInfoClick,
  onDrop,
  setIsDragging,
}: {
  slot: InventoryItem
  itemDef: ItemDefinition
  slotIndex: number
  inventory: HeroInventory
  isPending: boolean
  onUseItem: (slotIndex: number) => void
  onClose: () => void
  onInfoClick: () => void
  onDrop: (slotIndex: number) => void
  setIsDragging: (dragging: boolean) => void
}) => {
  const isBeacon = slot.itemDefId === "clone-activation-beacon"
  const isEquipment = itemDef.type === "equipment"
  const isSpecial = itemDef.type === "special"
  const usable = !isSpecial && (isEquipment || canUseItem(inventory, slotIndex))
  const onCooldown = slot.currentCooldown > 0

  // Touch drag state
  const dragStartY = useRef<number | null>(null)
  const isDragging = useRef(false)
  const [dragOffset, setDragOffset] = useState(0)

  const handleClick = () => {
    if (!usable || isDragging.current) return
    onUseItem(slotIndex)
    // Don't close if item requires targeting (teleport/lure)
    const needsTargeting = itemDef.effects.some((e) => e.type === "teleport" || e.type === "lure")
    if (!needsTargeting) {
      onClose()
    }
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isBeacon) return // Cannot drag beacon
    dragStartY.current = e.touches[0].clientY
    isDragging.current = false
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isBeacon || dragStartY.current === null) return

    const currentY = e.touches[0].clientY
    const deltaY = currentY - dragStartY.current

    // Only track upward drags (negative deltaY)
    if (deltaY < 0) {
      isDragging.current = true
      setIsDragging(true)
      setDragOffset(deltaY)
    }
  }

  const handleTouchEnd = () => {
    if (isBeacon || dragStartY.current === null) {
      dragStartY.current = null
      setDragOffset(0)
      setIsDragging(false)
      return
    }

    // If dragged up more than 80px, drop the item
    const DROP_THRESHOLD = -80
    if (dragOffset < DROP_THRESHOLD) {
      // Play armor drop sound for equipment
      if (itemDef.type !== "consumable") {
        playArmorDropSound()
      }
      onDrop(slotIndex)
      onClose()
    }

    // Reset drag state
    dragStartY.current = null
    isDragging.current = false
    setDragOffset(0)
    setIsDragging(false)
  }

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        disabled={!usable}
        className={`relative overflow-hidden transition-all rounded-lg flex items-center justify-center ${
          isPending
            ? "ring-2 ring-cyan-400 ring-offset-2 ring-offset-slate-900 animate-pulse"
            : usable
              ? "active:scale-95"
              : ""
        }`}
        style={{
          width: 70,
          height: 70,
          backgroundColor: itemDef.iconColor,
          border: isPending
            ? "3px solid #00CED1"
            : isBeacon
              ? "3px solid #3b82f6"
              : `3px solid ${usable ? "#fbbf24" : "#6b7280"}`,
          boxShadow: isPending
            ? "0 0 12px rgba(0, 206, 209, 0.6)"
            : isBeacon
              ? "0 0 8px rgba(59, 130, 246, 0.4)"
              : undefined,
          transform: dragOffset < 0 ? `translateY(${dragOffset}px)` : undefined,
          opacity: dragOffset < -80 ? 0.5 : 1,
        }}
      >
        {/* Item icon */}
        <ItemSpriteIcon itemDef={itemDef} size={56} />

        {/* Beacon charges badge */}
        {isBeacon && (
          <span
            className="absolute bottom-1 right-1 bg-blue-600/90 text-white font-bold flex items-center justify-center z-10 text-xs px-1.5 py-0.5 rounded"
          >
            {slot.charges || 0}
          </span>
        )}

        {/* Quantity badge (for non-beacon items) */}
        {!isBeacon && slot.quantity > 1 && (
          <span
            className="absolute bottom-1 right-1 bg-black/80 text-white font-bold flex items-center justify-center z-10 text-xs px-1.5 py-0.5 rounded"
          >
            {slot.quantity}
          </span>
        )}

        {/* Cooldown overlay */}
        {onCooldown && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-20 rounded-md">
            <span className="text-white font-bold text-lg">
              {Math.ceil(slot.currentCooldown / 60)}s
            </span>
          </div>
        )}
      </button>

      {/* Info button - positioned outside the item button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onInfoClick()
        }}
        className="absolute -top-1 -right-1 w-6 h-6 bg-slate-700 hover:bg-slate-600 active:bg-slate-500 rounded-full flex items-center justify-center z-10 border border-white/20 transition-colors"
      >
        <span className="text-white/80 text-xs font-bold">i</span>
      </button>
    </div>
  )
}

export const MobileInventoryPanel = ({
  isOpen,
  onClose,
  inventory,
  onUseItem,
  onDropItem,
  pendingItemSlot,
}: MobileInventoryPanelProps) => {
  const [drilldownSlotIndex, setDrilldownSlotIndex] = useState<number | null>(null)
  const [isDraggingItem, setIsDraggingItem] = useState(false)

  if (!isOpen) return null

  const drilldownSlot = drilldownSlotIndex !== null ? inventory.slots[drilldownSlotIndex] : null
  const drilldownItemDef = drilldownSlot ? getItemDefinition(drilldownSlot.itemDefId) : null

  // Handle closing - reset drilldown state
  const handleClose = () => {
    setDrilldownSlotIndex(null)
    onClose()
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[60]"
        onClick={handleClose}
      />

      {/* Bottom sheet container */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[70] animate-slide-up"
        style={{
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-t-2xl border-t border-x border-amber-500/40 shadow-lg shadow-black/50">
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 rounded-full bg-white/30" />
          </div>

          {drilldownSlot && drilldownItemDef && drilldownSlotIndex !== null ? (
            // Drilldown view - no header, just content with back button
            <ItemDetailView
              itemDef={drilldownItemDef}
              slot={drilldownSlot}
              slotIndex={drilldownSlotIndex}
              onBack={() => setDrilldownSlotIndex(null)}
              onDrop={onDropItem}
            />
          ) : (
            <>
              {/* Header with title and close button */}
              <div className="flex items-center justify-center relative px-4 pb-3 border-b border-white/10">
                <span className="text-amber-200 font-bold text-base">Inventory</span>
                <button
                  onClick={handleClose}
                  className="absolute right-4 p-2 rounded-lg hover:bg-white/10 transition-colors active:bg-white/20"
                >
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4L12 12M12 4L4 12" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Inventory grid - 3x2 layout with item names below */}
              <div className="p-4 relative">
                {/* Drop indicator - shown when dragging */}
                {isDraggingItem && (
                  <div className="absolute inset-x-0 top-0 flex items-center justify-center py-3 bg-red-500/20 border-b-2 border-red-500/50 z-10">
                    <div className="flex items-center gap-2">
                      <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 12V4M8 4L5 7M8 4L11 7" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="text-red-400 font-semibold text-sm">Drag up to drop item</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-4 justify-items-center">
                  {[0, 1, 2, 3, 4, 5].map((i) => {
                    const slot = inventory.slots[i]
                    const itemDef = slot ? getItemDefinition(slot.itemDefId) : null

                    if (!slot || !itemDef) {
                      return (
                        <div key={i} className="flex flex-col items-center gap-1">
                          <div
                            className="w-[70px] h-[70px] bg-gray-800/60 rounded-lg border-2 border-gray-600"
                          />
                          <span className="text-white/40 text-xs font-medium text-center">
                            Empty
                          </span>
                        </div>
                      )
                    }

                    const isPending = pendingItemSlot === i

                    return (
                      <div key={i} className="flex flex-col items-center gap-1">
                        <ItemButton
                          slot={slot}
                          itemDef={itemDef}
                          slotIndex={i}
                          inventory={inventory}
                          isPending={isPending}
                          onUseItem={onUseItem}
                          onClose={handleClose}
                          onInfoClick={() => setDrilldownSlotIndex(i)}
                          onDrop={onDropItem}
                          setIsDragging={setIsDraggingItem}
                        />
                        <span className={`text-xs font-medium text-center max-w-[80px] leading-tight ${rarityColors[itemDef.rarity]}`}>
                          {itemDef.name}
                        </span>
                      </div>
                    )
                  })}
                </div>

                {/* Hint text */}
                <p className="text-center text-white/40 text-xs mt-4">
                  Tap to use • Drag up to drop • Tap info for details
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
